import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserProfile } from './useUserProfile';
import { toast } from './use-toast';

interface TeamMember {
  id: string;
  user_id: string;
  role: 'admin' | 'employee';
  created_at: string;
  email: string;
  full_name: string;
  phone: string | null;
  avatar_url: string | null;
  isCreator: boolean;
}

interface InviteUserData {
  email: string;
  full_name: string;
  phone?: string;
  role: 'admin' | 'employee';
}

export function useTeamManagement() {
  const { tenant } = useUserProfile();
  const queryClient = useQueryClient();

  // Query: Listar membros da equipe
  const { data: teamMembers, isLoading } = useQuery({
    queryKey: ['team_members', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];

      // Buscar user_roles da tenant
      const { data: userRoles, error } = await supabase
        .from('user_roles')
        .select(`
          id,
          user_id,
          role,
          created_at
        `)
        .eq('tenant_id', tenant.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Buscar criador da tenant
      const { data: creatorId } = await supabase.rpc('get_tenant_creator', {
        p_tenant_id: tenant.id
      });

      // Para cada user_role, buscar email, profile e marcar criador
      const membersWithDetails = await Promise.all(
        userRoles.map(async (ur) => {
          // Buscar email via RPC
          const { data: email } = await supabase.rpc('get_user_email', {
            p_user_id: ur.user_id
          });

          // Buscar profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, phone, avatar_url')
            .eq('user_id', ur.user_id)
            .single();

          return {
            ...ur,
            email: email || '',
            full_name: profile?.full_name || '',
            phone: profile?.phone || null,
            avatar_url: profile?.avatar_url || null,
            isCreator: ur.user_id === creatorId,
          };
        })
      );

      return membersWithDetails as TeamMember[];
    },
    enabled: !!tenant?.id,
  });

  // Mutation: Convidar novo usuário
  const inviteUser = useMutation({
    mutationFn: async (userData: InviteUserData) => {
      if (!tenant?.id) throw new Error('Tenant não encontrado');

      // 1. Validar via função SQL (verifica limites e duplicatas)
      const { error: validationError } = await supabase.rpc('invite_team_member', {
        p_email: userData.email,
        p_full_name: userData.full_name,
        p_phone: userData.phone || '',
        p_role: userData.role,
        p_tenant_id: tenant.id,
      });

      if (validationError) throw validationError;

      // 2. Criar usuário via Supabase Auth (envia email de convite)
      const { data: authData, error: authError } = await supabase.auth.admin.inviteUserByEmail(
        userData.email,
        {
          data: {
            full_name: userData.full_name,
            is_demo_user: false,
          },
          redirectTo: `${window.location.origin}/auth`,
        }
      );

      if (authError) throw authError;

      // 3. Criar profile
      const { error: profileError } = await supabase.from('profiles').insert({
        user_id: authData.user.id,
        full_name: userData.full_name,
        phone: userData.phone || null,
      });

      if (profileError) throw profileError;

      // 4. Criar user_role
      const { error: roleError } = await supabase.from('user_roles').insert({
        user_id: authData.user.id,
        tenant_id: tenant.id,
        role: userData.role,
      });

      if (roleError) throw roleError;

      return authData.user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team_members'] });
      toast({
        title: 'Convite enviado!',
        description: 'Um email de convite foi enviado para o novo usuário.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao enviar convite',
        description: error.message || 'Não foi possível enviar o convite.',
        variant: 'destructive',
      });
    },
  });

  // Mutation: Atualizar role do usuário
  const updateUserRole = useMutation({
    mutationFn: async ({ userId, newRole }: { userId: string; newRole: 'admin' | 'employee' }) => {
      if (!tenant?.id) throw new Error('Tenant não encontrado');

      // Verificar se é o criador
      const member = teamMembers?.find((m) => m.user_id === userId);
      if (member?.isCreator) {
        throw new Error('Não é possível alterar a permissão do criador da empresa');
      }

      const { error } = await supabase
        .from('user_roles')
        .update({ role: newRole })
        .eq('user_id', userId)
        .eq('tenant_id', tenant.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team_members'] });
      toast({
        title: 'Permissão atualizada!',
        description: 'O nível de acesso do usuário foi alterado com sucesso.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar permissão',
        description: error.message || 'Não foi possível atualizar a permissão.',
        variant: 'destructive',
      });
    },
  });

  // Mutation: Remover usuário
  const removeUser = useMutation({
    mutationFn: async (userId: string) => {
      if (!tenant?.id) throw new Error('Tenant não encontrado');

      // Verificar se é o criador
      const member = teamMembers?.find((m) => m.user_id === userId);
      if (member?.isCreator) {
        throw new Error('Não é possível remover o criador da empresa');
      }

      // Remover user_role
      const { error: roleError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('tenant_id', tenant.id);

      if (roleError) throw roleError;

      // Verificar se o usuário tem outras roles
      const { count } = await supabase
        .from('user_roles')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      // Se não tiver outras roles, deletar o usuário
      if (count === 0) {
        const { error: deleteError } = await supabase.auth.admin.deleteUser(userId);
        if (deleteError) throw deleteError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team_members'] });
      toast({
        title: 'Usuário removido!',
        description: 'O usuário foi removido da empresa com sucesso.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao remover usuário',
        description: error.message || 'Não foi possível remover o usuário.',
        variant: 'destructive',
      });
    },
  });

  return {
    teamMembers,
    isLoading,
    inviteUser,
    updateUserRole,
    removeUser,
    currentUsersCount: teamMembers?.length || 0,
  };
}
